import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, IconButton, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { filtersDefault } from '@/config/filters.config'
import { getClasses } from '@/store/dashboard/actions'
import { localUserQuestionUpdate } from '@/store/questions/actions'
import DashboardService from '@/services/DashboardService'
import { HelperService } from '@/services/helper.service'
import { localDateTime } from '@/utils/formatUtils'
import DatagridUser from '@/components/UI/Users/DatagridUser'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import NewDashboardFilters from '@/components/UI/Dashboard/DashboardFilters/NewDashboardFilters'
import BasicDrawer from '@/components/UI/Drawer'
import DashboardNavigation from '../Dashboard/pages/DashboardNavigation'
import DeleteLearnerQuestionsPopup from './popups/DeleteLearnerQuestionsPopup'
import KnowledgeCreateFromScratch from '@/routes/Knowledge/KnowledgeCreateFromScratch/KnowledgeCreateFromScratch'
import LearnerQuestionDetails from './LearnerQuestionDetails'
import './style.scss'

const service = new DashboardService()
const helperService = new HelperService()

const LearnerQuestions = () => {
  const { t } = useTranslation('translation')

  const QUESTION_TYPES = [
    { value: '', label: t('All Questions') },
    { value: 'unanswered', label: t('Unanswered Questions') },
    { value: 'answered', label: t('Answered Questions') },
  ]
  const PerPage = 10

  const [paginationState, setPaginationState] = useState<any>(null)
  const [learnerQuestions, setLearnerQuestions] = useState<any[]>([])
  const [classes, setClasses] = useState([])
  const [currentRow, setCurrentRow] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [addToKnowledgeDrawerOpen, setAddToKnowledgeDrawerOpen] =
    useState(false)

  // Drawer state for KnowledgeCreateFromScratch (like FlashCards pattern)
  const [selectedIntent, setSelectedIntent] = useState<any>(null)

  const dispatch = useDispatch()
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PerPage,
  })

  const [filters, setFilters] = useState({
    question_type: QUESTION_TYPES[1],
    class_id: null,
    to_date: filtersDefault.toDate,
    from_date: filtersDefault.fromDate,
  })

  // Track initial load to prevent double fetching
  const initialLoadRef = useRef(true)

  // Row count ref pattern for stable pagination
  const rowCountRef = useRef(paginationState?.total_items || 0)
  const rowCount = useMemo(() => {
    if (paginationState?.total_items !== undefined) {
      rowCountRef.current = paginationState.total_items
    }
    return rowCountRef.current
  }, [paginationState?.total_items])

  const fetchLearnerQuestions = useCallback(async () => {
    setLoading(true)
    try {
      const filter: any = {
        answer_status: filters?.question_type?.value || null,
        class_id: filters?.class_id || null,
        page: paginationModel.page + 1, // API is 1-based, DataGrid is 0-based
        page_size: paginationModel.pageSize,
        filter_from_date: filters?.from_date || null,
        filter_to_date: filters?.to_date || null,
        sort_field: 'updated_at',
        sort_order: 'dsc',
      }
      const res = await service.getLearnerQuestions(filter)

      if (res.status === 200) {
        setLearnerQuestions(res.data.data.threads)
        setPaginationState(res.data.data.pagination)
      }
    } finally {
      setLoading(false)
    }
  }, [filters, paginationModel])

  const getClass = async () => {
    const { classes }: any = await dispatch(getClasses())
    setClasses(classes)
  }

  // Initial load: fetch classes and discussions once
  useEffect(() => {
    const initLoad = async () => {
      await getClass()
      await fetchLearnerQuestions()
      initialLoadRef.current = false
    }
    initLoad()
  }, [])

  // Subsequent filter/pagination changes
  useEffect(() => {
    if (initialLoadRef.current) return
    fetchLearnerQuestions()
  }, [filters, paginationModel, fetchLearnerQuestions])

  const hasFilterUpdated = async (e: any) => {
    setFilters(prev => ({
      ...prev,
      from_date: e.filter_from_date ?? prev.from_date,
      to_date: e.filter_to_date ?? prev.to_date,
      class_id: e.filter_class_ids ?? prev.class_id,
    }))
    // Reset to first page when filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }

  const removeLearnerQuestion = async (id: any[]) => {
    for (let index = 0; index < id.length; index++) {
      await service.deleteDiscussion(id[index])
    }
    setShowDeleteModal(false)
    setSelectedRows([])
    helperService.showToasterIcon(t('Successful remove'), 'success')
    fetchLearnerQuestions()
  }

  const handleOpenDrawer = (row: any) => {
    if (row.post_count > 0) {
      setCurrentRow(row)
      setDrawerOpen(true)
    }
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
  }

  const columns: GridColDef[] = [
    {
      field: 'text',
      headerName: t('Question'),
      flex: 2,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 1 }}>
          {row?.answer_status === 'answered' && (
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 0.5 }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }}
              />
              <Typography variant="caption" color="success.main">
                {t('Answer Added to Knowledge Base')}
              </Typography>
            </Box>
          )}
          {row.text}
        </Box>
      ),
    },
    {
      field: 'author_email',
      headerName: t('Asked by'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <DatagridUser
          user={{
            _id: row?.author_id || row?.author_email,
            email: row?.author_email,
            first_name: row?.author_first_name,
            last_name: row?.author_last_name,
            profile_image: row?.author_profile_image,
          }}
        />
      ),
    },
    {
      field: 'updated_at',
      headerName: t('Date'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      valueFormatter: (value: string) => localDateTime(value),
    },
    {
      field: 'class_id',
      headerName: t('Class'),
      flex: 1,
      minWidth: 100,
      sortable: false,
      valueFormatter: (value: string) =>
        classes.find(e => e.value === value)?.label || '-',
    },
    {
      field: 'post_count',
      headerName: t('Suggested Answers'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: row.post_count > 0 ? 'pointer' : 'default',
            color: row.post_count > 0 ? 'primary.main' : 'text.secondary',
          }}
          onClick={() => handleOpenDrawer(row)}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
          {row.post_count}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      headerAlign: 'center',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <PermissionWrapper>
          <Box sx={{ display: 'flex' }}>
            <IconButton
              size="small"
              onClick={() => createFromScratchHandler(row)}
              sx={{ padding: '8px' }}
              aria-label={t('Add to knowledge')}
            >
              <AddIcon fontSize="small" color="primary" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedRows([row._id])
                setShowDeleteModal(true)
              }}
              sx={{ padding: '8px' }}
              aria-label={t('Delete question')}
            >
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        </PermissionWrapper>
      ),
    },
  ]

  // Add id to rows for DataGrid
  const gridRows = useMemo(
    () =>
      learnerQuestions.map((row: any) => ({
        id: row._id,
        ...row,
      })),
    [learnerQuestions],
  )

  const createFromScratchHandler = async (row: any) => {
    let approvedPosts: any[] = []
    const res = await service.getDiscussionDetails(row._id, '')
    if (res.status === 200) {
      const details = res.data.data
      if (details?.posts.length > 0) {
        approvedPosts = details?.posts.filter(
          (p: any) => p?.approval_status === 'accepted',
        )
      }
    }

    // If editing existing intent
    if (row && row.intent_id) {
      setCurrentRow({ ...row })
      // Fetch existing intent for editing
      setSelectedIntent({ _id: row.intent_id })
      setAddToKnowledgeDrawerOpen(true)
      return
    }

    // Creating new - pre-populate questions and responses via redux state
    setCurrentRow({ ...row, intent_id: null })

    // Pre-populate question from learner's question
    if (row.text) {
      dispatch(
        localUserQuestionUpdate('questions', [
          {
            text: row.text,
          },
        ]),
      )
    }

    // Pre-populate responses from approved posts
    if (approvedPosts && approvedPosts.length > 0) {
      dispatch(
        localUserQuestionUpdate(
          'responses',
          approvedPosts.map((p: any) => ({
            text: p.text,
            group_ids: [],
          })),
        ),
      )
    }

    setSelectedIntent(null) // Create mode, no existing intent
    setAddToKnowledgeDrawerOpen(true)
  }

  const handleCloseAddToKnowledgeDrawer = () => {
    setAddToKnowledgeDrawerOpen(false)
    setSelectedIntent(null)
    // Refresh data after closing
    fetchLearnerQuestions()
  }

  return (
    <section className="dashborad-wraper">
      <NewDashboardFilters hasFilterUpdated={hasFilterUpdated} />

      <DashboardNavigation />

      <div className="all-knowledge-search responses-by-ai tw-pt-4 tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-justify-between tw-items-center">
          <div>
            <Typography variant="body2" color="text.secondary">
              {t("Review and manage learners' unanswered questions")}
            </Typography>
          </div>
        </div>

        <DeleteLearnerQuestionsPopup
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false)
            setSelectedRows([])
          }}
          onDelete={(e: any, id: any[]) => removeLearnerQuestion(id)}
          selectedRows={selectedRows}
        />

        <div className="learner-question-tab position-relative">
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              loading={loading}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                  py: 1.5,
                  alignItems: 'flex-start',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f9fafb',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                },
              }}
              localeText={{
                noRowsLabel: t('There are no records to display'),
              }}
            />
          </Box>

          {/* Drawer for LearnerQuestionDetails */}
          <BasicDrawer
            open={drawerOpen}
            onClose={handleCloseDrawer}
            title={t('Suggested Answers')}
          >
            {currentRow && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('Question')}
                </Typography>
                <Typography variant="body1">{currentRow.text}</Typography>
              </Box>
            )}
            {currentRow && (
              <LearnerQuestionDetails
                data={currentRow}
                onEdit={(postItem: any) => {
                  // For accepted posts that already have an intent, edit the existing intent
                  if (currentRow?.intent_id) {
                    setSelectedIntent({ _id: currentRow.intent_id })
                  } else {
                    // Pre-populate for creating new
                    if (currentRow.text) {
                      dispatch(
                        localUserQuestionUpdate('questions', [
                          { text: currentRow.text },
                        ]),
                      )
                    }
                    if (postItem) {
                      dispatch(
                        localUserQuestionUpdate('responses', [
                          { text: postItem.text, group_ids: [] },
                        ]),
                      )
                    }
                    setSelectedIntent(null)
                  }
                  setAddToKnowledgeDrawerOpen(true)
                }}
                onAdd={(postItem: any) => {
                  // Pre-populate question from learner's question
                  if (currentRow.text) {
                    dispatch(
                      localUserQuestionUpdate('questions', [
                        { text: currentRow.text },
                      ]),
                    )
                  }
                  // Pre-populate response from selected post
                  if (postItem) {
                    dispatch(
                      localUserQuestionUpdate('responses', [
                        { text: postItem.text, group_ids: [] },
                      ]),
                    )
                  }
                  setSelectedIntent(null) // Create mode
                  setAddToKnowledgeDrawerOpen(true)
                }}
              />
            )}
          </BasicDrawer>

          {/* Drawer for KnowledgeCreateFromScratch */}
          <BasicDrawer
            open={addToKnowledgeDrawerOpen}
            onClose={handleCloseAddToKnowledgeDrawer}
            title={
              selectedIntent ? t('Edit Flash Card') : t('Create new Flash Card')
            }
            width={750}
          >
            <KnowledgeCreateFromScratch
              resetEditKnowledgehandler={handleCloseAddToKnowledgeDrawer}
              selectedIntent={selectedIntent}
              editMode={!!selectedIntent}
              hideHeader
              drawerMode
            />
          </BasicDrawer>
        </div>
      </div>
    </section>
  )
}

export default LearnerQuestions
